import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { earlyPaymentCalculation } from '../api';
import { useAuth } from '../hooks/useAuth';
import {
  getPaymentTypeLabel,
  IEarlyPaymentCalculationResult,
  PricingUnitEnum,
} from '../utils/types';
import LoadingSpinner from './LoadingSpinner';

export function EarlyPaymentCalculationTable(props: {
  salesIndividualId: number;
}) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);
  const [earlyPaymentResult, setEarlyPaymentResult] = useState<
    IEarlyPaymentCalculationResult[]
  >([]);

  useEffect(() => {
    fetchEarlyPaymentCalculation(props.salesIndividualId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.salesIndividualId]);

  const formatDate = (date?: string) => {
    const dateOptions: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    };

    if (date) return new Date(date).toLocaleDateString('tr-TR', dateOptions);
    return '-';
  };

  const fetchEarlyPaymentCalculation = async (salesIndividualId: number) => {
    try {
      const res = await earlyPaymentCalculation(salesIndividualId, token);
      const data = await res.json();
      if (res?.status === 200) {
        setEarlyPaymentResult(data);
      } else {
        throw data;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ ml: 3, mr: 6 }}>
      {earlyPaymentResult && earlyPaymentResult.length > 0 && (
        <Box>
          {loading ? (
            <LoadingSpinner loadingText={t('accountContainer.submitting')} />
          ) : (
            <Box>
              <Typography sx={{ mb: 2 }}>
                {t('earlyPaymentCalculationTable.earlyPaymentDiscounts')}
              </Typography>
              <TableContainer component={Paper} sx={{ borderRadius: '0px' }}>
                <Table sx={{ width: '100%' }} aria-label="simple table">
                  <TableHead sx={{ backgroundColor: '#657283' }}>
                    <TableRow>
                      <TableCell
                        sx={{ color: '#FFFFFF', border: '1px solid #fff' }}
                        align="left"
                      >
                        {t('earlyPaymentCalculationTable.duePaymentDate')}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: '#FFFFFF',
                          border: '1px solid #fff',
                        }}
                        padding={'none'}
                        align="center"
                      >
                        {t('earlyPaymentCalculationTable.paymentType')}
                      </TableCell>
                      <TableCell
                        sx={{ color: '#FFFFFF', border: '1px solid #fff' }}
                        align="center"
                      >
                        {t('earlyPaymentCalculationTable.amount')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {earlyPaymentResult?.map(
                      (row: IEarlyPaymentCalculationResult, i: number) => (
                        <TableRow key={i}>
                          <TableCell
                            sx={{ border: '1px solid #ccc' }}
                            component="th"
                            scope="row"
                            align="left"
                          >
                            {formatDate(row?.duePaymentDate) || '-'}
                          </TableCell>

                          <TableCell
                            sx={{ border: '1px solid #ccc' }}
                            component="th"
                            scope="row"
                            align="center"
                          >
                            {getPaymentTypeLabel(row.paymentType)}
                          </TableCell>
                          <TableCell
                            sx={{ border: '1px solid #ccc' }}
                            component="th"
                            scope="row"
                            align="center"
                          >
                            {row.pricingUnit === PricingUnitEnum.PERCENT
                              ? `%${row.amount || '-'}`
                              : row.amount || '-'}
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
